import { useState, useRef, useEffect } from "react";
import { VideoCameraOutlined, DeleteOutlined } from "@ant-design/icons";
import { X } from "lucide-react";
import { notification } from "antd";
import { useGeneratePresignedUrlMutation } from "../../store/api/apiSlice";
import {
  uploadToPresignedUrl,
  generateFileName,
} from "../../utils/presignedUploader";

const VideoRecordModal = ({ isOpen, onClose, onVideoSave }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoStream, setVideoStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const timerRef = useRef(null);
  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen && !isRecording && !recordedVideo) {
      startVideoPreview();
    }
    return () => {
      stopVideoStream();
    };
  }, [isOpen]);

  const startVideoPreview = async () => {
    try {
      // Stop any existing streams
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }

      // Video constraints to reduce file size: max 480p resolution for smaller files
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 854, max: 854 },
          height: { ideal: 480, max: 480 },
          frameRate: { ideal: 24, max: 24 },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 32000,
        },
      });

      setVideoStream(stream);
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      notification.error({
        message: "Camera Error",
        description:
          error.message ||
          "Failed to access camera. Please check your permissions.",
      });
    }
  };

  const stopVideoStream = () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
      setVideoStream(null);
    }
  };

  const startRecording = async () => {
    try {
      // Get fresh stream with size constraints for smaller files
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 854, max: 854 },
          height: { ideal: 480, max: 480 },
          frameRate: { ideal: 24, max: 24 },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 32000,
        },
      });
      setVideoStream(stream);

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      // Check supported mime types for iOS
      let mimeType = "";
      if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
        mimeType = "video/webm;codecs=vp9";
      } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
        mimeType = "video/webm;codecs=vp8";
      } else if (MediaRecorder.isTypeSupported("video/webm")) {
        mimeType = "video/webm";
      } else if (MediaRecorder.isTypeSupported("video/mp4")) {
        mimeType = "video/mp4";
      } else {
        mimeType = ""; // Let browser choose
      }

      // Create and configure MediaRecorder with lower bitrate constraints for smaller files
      const options = {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 1000000, // 1 Mbps - significantly reduces file size
        audioBitsPerSecond: 64000, // 64 kbps - lower audio bitrate
      };

      // Remove bitrate constraints for MP4 as they may not be supported
      if (mimeType === "video/mp4") {
        delete options.videoBitsPerSecond;
        delete options.audioBitsPerSecond;
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType || "video/webm" });
        setRecordedVideo(blob);
        const videoURL = URL.createObjectURL(blob);

        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
          videoPreviewRef.current.src = videoURL;
          videoPreviewRef.current.muted = false;
        }
      };

      // Start recording
      mediaRecorder.start(1000);
      setIsRecording(true);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      notification.error({
        message: "Recording Error",
        description:
          error.message ||
          "Failed to start recording. Please check your camera permissions.",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      stopVideoStream();
    }
  };

  const handleUseVideo = async () => {
    if (!recordedVideo) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Generate filename and get content type
      const contentType = recordedVideo.type || "video/webm";
      const fileName = generateFileName(contentType, "recorded-video");

      // Get presigned URL from backend
      const presignedResponse = await generatePresignedUrl({
        fileName,
        contentType,
        expiresIn: 600, // 10 minutes
      }).unwrap();

      if (!presignedResponse?.url) {
        throw new Error("Failed to get presigned URL");
      }

      // Upload directly to S3
      await uploadToPresignedUrl(
        presignedResponse.url,
        recordedVideo,
        contentType,
        (progress) => setUploadProgress(progress),
      );

      // Use the fileUrl from the response
      const fileUrl = presignedResponse.fileUrl;

      // Close modal immediately
      onClose();

      // Call callback with the file URL
      if (fileUrl) {
        onVideoSave(fileUrl);
      } else {
        throw new Error("No file URL returned from server");
      }
    } catch (error) {
      console.error("Upload error:", error);
      notification.error({
        message: "Error",
        description:
          error.message || "Failed to upload video. Please try again.",
      });
      setIsUploading(false);
      setUploadProgress(0);
      // Optionally reopen modal on error
      // setIsOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteVideo = () => {
    if (videoPreviewRef.current) {
      videoPreviewRef.current.pause();
      videoPreviewRef.current.src = "";
    }
    if (recordedVideo) {
      URL.revokeObjectURL(recordedVideo);
    }
    setRecordedVideo(null);
    setRecordingTime(0);
    if (onVideoSave) onVideoSave(null);
    startVideoPreview();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50">
      <div className="relative w-full h-full">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute h-10 w-10 border-0 top-4 right-4 z-10 text-white bg-black bg-opacity-50 p-2 rounded-full hover:bg-opacity-70"
        >
          <X size={24} />
        </button>

        {/* Video container taking full height */}
        <div className="w-full h-full relative">
          <video
            ref={videoPreviewRef}
            autoPlay
            playsInline
            // muted={!recordedVideo}
            controls={!!recordedVideo}
            className="w-full h-full object-cover"
          />

          {/* Timer overlay */}
          <div className="absolute top-4 left-4 bg-black bg-opacity-50 px-3 py-1 rounded-full">
            <span className="text-white text-sm">
              {formatTime(recordingTime)}
            </span>
          </div>

          {/* Delete button for recorded video */}
          {recordedVideo && (
            <div className="absolute top-4 right-16">
              <button
                onClick={handleDeleteVideo}
                className="bg-red-500 h-10 w-10 border-0 text-white p-2 rounded-full hover:bg-red-600"
              >
                <DeleteOutlined />
              </button>
            </div>
          )}

          {/* Control buttons absolute positioned at bottom */}
          <div className="absolute bottom-8 left-0 right-0 px-6">
            {!isRecording && !recordedVideo ? (
              <button
                onClick={() => {
                  startRecording().catch((error) => {
                    console.error("Recording error:", error);
                    notification.error({
                      message: "Recording Error",
                      description:
                        "Failed to start recording. Please try again.",
                    });
                  });
                }}
                className="h-12 cursor-pointer max-w-xl mx-auto bg-blue text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
              >
                <VideoCameraOutlined />
                Start Recording
              </button>
            ) : isRecording ? (
              <div className="flex gap-4 max-w-xl mx-auto">
                <button
                  onClick={() => {
                    stopRecording();
                    handleDeleteVideo();
                  }}
                  className="flex-1 bg-[#333333] text-white py-4 rounded-xl hover:bg-opacity-90 transition-colors border-0"
                >
                  Cancel
                </button>
                <button
                  onClick={stopRecording}
                  className="flex-1 py-4 cursor-pointer max-w-xl mx-auto bg-blue text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                >
                  Stop Recording
                </button>
              </div>
            ) : recordedVideo ? (
              <div className="text-center">
                <button
                  onClick={handleUseVideo}
                  className="flex-1 py-4 cursor-pointer max-w-xl mx-auto bg-blue text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                >
                  Use This Video
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoRecordModal;
